// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import PasswordInput from "@/UI/inputs/PasswordInput";
import PhoneInput from "@/UI/inputs/PhoneInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * Phone and password, and nothing else: there is no OTP in this product, no SMS provider, and no
 * self-service reset — an admin's password is issued to them by another admin.
 *
 * The API answers with a token only. The account itself comes from `/auth/me`, which `DashboardLayout`
 * asks for on the other side of the gate — so there is no second screen between signing in and work,
 * the way `clients-web` has an organization to pick.
 */
export default function LoginScreen() {
    const t = useTranslation();
    const token = useAuth((state) => state.token);
    const setToken = useAuth((state) => state.setToken);

    const [phone, setPhone] = useState<string | undefined>("");
    const [password, setPassword] = useState("");
    const [formError, setFormError] = useState<string | null>(null);

    const { data, loading, error, invoke } = useFetch<{ token: string }>("/auth/login", { method: "POST" }, false);

    useEffect(() => {
        if (data?.token) setToken(data.token);
    }, [data, setToken]);

    const handleSubmit = () => {
        setFormError(null);
        // The country picker leaves the calling code in place, so an untouched field is "+966" alone.
        const cleanPhone = (phone ?? "").replace(/\s/g, "");
        if (cleanPhone.length < 8) return setFormError(t.auth.phoneRequired);
        if (!password) return setFormError(t.auth.passwordRequired);
        invoke({ method: "POST", data: { phone: cleanPhone, password } });
    };

    // Set by the effect above on success, and by the persisted store on a reload.
    if (token) return <Navigate to="/" replace />;

    return (
        <main className="login-screen">
            <section className="login-screen__card">
                <img className="login-screen__logo" src={constants.logo.round} alt="Lozicore" />
                <h1 className="login-screen__title">{t.auth.title}</h1>
                <p className="login-screen__subtitle">{t.auth.subtitle}</p>

                <PhoneInput value={phone} onChange={setPhone} label={t.auth.phone} disabled={loading} />
                <PasswordInput
                    value={password}
                    onChange={setPassword}
                    label={t.auth.password}
                    placeholder={t.auth.passwordPlaceholder}
                    disabled={loading}
                    onEnter={handleSubmit}
                />

                {(formError || error) && <p className="login-screen__error">{formError ?? error}</p>}

                <button type="button" className="login-screen__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.auth.login}
                </button>
            </section>
        </main>
    );
}
